import Vue, { readonly, ref } from 'vue';
import { type ToastItem, type AddToastPayload } from './types';
import { ToastEmit } from '../Toast.types';
import ToastContainer from '../ToastContainer.vue';

const TOAST_REMOVE_DELAY: number = 5000;
const CONTAINER_DESTROY_DELAY: number = 5000;

const toasts = ref<ToastItem[]>([]);

let toastContainerInstance: InstanceType<ReturnType<typeof Vue.extend>> | null = null;
let destroyTimeoutId: ReturnType<typeof setTimeout> | null = null;
const containerZIndex = ref<number>(1000);

const genId = (type: string, title: string, description: string) => {
  return [type, title, description].filter(Boolean).join('_');
};

const initToastContainer = () => {
  if (destroyTimeoutId) {
    clearTimeout(destroyTimeoutId);
    destroyTimeoutId = null;
  }

  if (toastContainerInstance) return;

  const ToastContainerConstructor = Vue.extend(ToastContainer);
  toastContainerInstance = new ToastContainerConstructor();

  const container = document.createElement('div');
  document.body.append(container);
  toastContainerInstance?.$mount(container);
};

const destroyToastContainer = () => {
  if (toasts.value.length === 0) {
    destroyTimeoutId = setTimeout(() => {
      toastContainerInstance?.$destroy();
      toastContainerInstance?.$el?.remove();

      toastContainerInstance = null;
    }, CONTAINER_DESTROY_DELAY);
  }
};

export const useToastContainer = () => {
  const reset = () => {
    if (toasts.value.length === 0) {
      return;
    }

    toasts.value.forEach((toast) => {
      if (toast.timeoutId) {
        clearTimeout(toast.timeoutId);
      }
    });

    toasts.value = [];
    destroyToastContainer();
  };

  return {
    toasts: readonly(toasts),
    zIndex: readonly(containerZIndex),
    reset,
  };
};

const triggerEmitEvent = (emitName: string, id: string) => {
  const event = new CustomEvent('emitEvent', {
    detail: { emitName, id },
  });

  window.dispatchEvent(event);
};

const removeToast = (id: string, auto: boolean = false) => {
  const toast = toasts.value?.find((toast) => toast.id === id);

  if (!auto && toast && toast.timeoutId) {
    clearTimeout(toast.timeoutId);
  }

  toasts.value = toasts.value.filter((toast) => toast.id !== id);

  triggerEmitEvent(auto ? ToastEmit.LifeEnd : ToastEmit.Close, id);
  destroyToastContainer();
};

const getTimeoutId = ({ id, inline, duration }: { id: string; inline: boolean; duration: number }) => {
  if (!inline) {
    return setTimeout(() => removeToast(id, true), duration);
  }

  return null;
};

const showToast = ({
  type,
  title = '',
  description = '',
  closable = false,
  inline = false,
  duration = TOAST_REMOVE_DELAY,
  illustration = null,
  mobile = false,
  icon = true,
  zIndex = 1000,
  size,
}: AddToastPayload) => {
  containerZIndex.value = zIndex;

  initToastContainer();
  const id = genId(type, title, description);

  if (toasts.value.some((toast) => toast.id === id)) {
    return;
  }

  const timeoutId = getTimeoutId({ id, inline, duration });

  toasts.value.push({
    id,
    title,
    description,
    type,
    timeoutId,
    closable,
    inline,
    duration,
    illustration,
    mobile,
    icon,
    size,
  });

  return id;
};

export const useToast = () => {
  return {
    showToast,
    removeToast,
  };
};
